import { useEffect, useRef, useState } from 'react';
import { getCachedAudio } from './musicCache';
import { useMusic as useMusicContext } from '../music/MusicContext';
import {
  EXPLORE_UNKNOWN_PORTAL_LAYOUT,
  isExploreUnknownFairSliceMissionIndex,
} from '../exploreUnknownPortalLayout';
import { isExploreUnknownKendrexColonyMissionIndex } from '../config/exploreUnknownKendrexColony';
import {
  getCurrentPlayingAudio,
  setCurrentPlayingAudio,
  getCurrentFadeOutPromise,
  setCurrentFadeOutPromise,
  acquireTrackStartLock,
  stopAllAudio,
  registerAudioElement,
  unregisterAudioElement,
  incrementTrackRetain,
  decrementTrackRetain,
  scheduleTrackStopIfUnretained,
  cancelScheduledTrackStop,
} from './musicState';

const KNOWN_FACTIONS = [
  'Remnants of Humanity',
  'Exolot Dominion',
  'League of Shadows',
  'Ra\'s Children',
  'Asrah',
  'The Conspiracy',
  'The Corrupted Core',
  'The Covenant',
  'The Divine Syndicate',
  'The Forsaken Pact',
  'The Hollow',
  'The Iron Alliance',
  'The Last Crusade',
  'The Shadow Consortium',
  'The Silent Accord',
  'The Sentients'
];

const canonicalFactionByLowercase = KNOWN_FACTIONS.reduce((acc, faction) => {
  acc[faction.toLowerCase()] = faction;
  return acc;
}, {});

function normalizeFactionName(rawFaction) {
  if (!rawFaction || typeof rawFaction !== 'string') return null;

  // Normalize whitespace and apostrophes so DB/UI variants resolve consistently.
  const normalized = rawFaction
    .trim()
    .replace(/[’`´]/g, '\'')
    .replace(/\s+/g, ' ');

  return canonicalFactionByLowercase[normalized.toLowerCase()] || normalized;
}

// Combined factions mapping (main factions only, excluding Sentients)
const combinedFactions = {
  'The Conspiracy': ['League of Shadows', 'Asrah'],
  'The Corrupted Core': ['Exolot Dominion', 'Asrah'],
  'The Covenant': ['Ra\'s Children', 'League of Shadows'],
  'The Divine Syndicate': ['Ra\'s Children', 'Exolot Dominion'],
  'The Forsaken Pact': ['Remnants of Humanity', 'Asrah'],
  'The Hollow': ['Ra\'s Children', 'Asrah'],
  'The Iron Alliance': ['Remnants of Humanity', 'Exolot Dominion'],
  'The Last Crusade': ['Ra\'s Children', 'Remnants of Humanity'],
  'The Shadow Consortium': ['League of Shadows', 'Exolot Dominion'],
  'The Silent Accord': ['Remnants of Humanity', 'League of Shadows']
};

// Tutorial missions (chapter 1, missions 1–4) use rock_2.mp3
const TUTORIAL_MISSION_IDS = ['chapter_1_mission_1', 'chapter_1_mission_2', 'chapter_1_mission_3', 'chapter_1_mission_4'];
const TUTORIAL_MUSIC_TRACK = '/music/rock_2.mp3';

// Explore Unknown boss missions (e.g. mission 10 — cave boss) override the faction track with a
// dedicated boss theme. The boss index list is the single source of truth used by the map overlay
// to render the red warning triangle, so the visual + music stay in sync automatically.
const EXPLORE_BOSS_MUSIC_TRACK = '/music/explore_boss.mp3';

/** Fairgrounds map (`shadowverse_fair.jpg` slice); one track chosen at random per mission battle. */
const EXPLORE_UNKNOWN_FAIR_BATTLE_MUSIC_PATHS = Object.freeze(['/music/fair1.mp3', '/music/fair2.mp3']);

/** Ra's Children battle themes; one chosen at random, alternates on track end when possible. */
const RA_CHILDREN_BATTLE_MUSIC_PATHS = Object.freeze([
  '/music/playing_ra_2.mp3',
  '/music/ra_3.mp3'
]);

function isExploreUnknownBossMissionIndex(missionIndex) {
  if (missionIndex == null) return false;
  const idx = Number(missionIndex);
  if (!Number.isInteger(idx)) return false;
  const indices = EXPLORE_UNKNOWN_PORTAL_LAYOUT?.bossMissionIndices;
  return Array.isArray(indices) && indices.includes(idx);
}

/**
 * Prefer the fairgrounds map track already playing (shared audio cache) so map → battle
 * does not lose music when the random pick matches the ambient track.
 * @param {string|null} previousTrack
 * @returns {string}
 */
function pickExploreUnknownFairBattleTrackPath(previousTrack = null) {
  const currentAudio = getCurrentPlayingAudio();
  if (currentAudio && !currentAudio.paused) {
    for (const fairPath of EXPLORE_UNKNOWN_FAIR_BATTLE_MUSIC_PATHS) {
      if (getCachedAudio(fairPath) === currentAudio) {
        return fairPath;
      }
    }
  }
  if (previousTrack && EXPLORE_UNKNOWN_FAIR_BATTLE_MUSIC_PATHS.includes(previousTrack)) {
    const others = EXPLORE_UNKNOWN_FAIR_BATTLE_MUSIC_PATHS.filter((path) => path !== previousTrack);
    if (others.length > 0) {
      return others[Math.floor(Math.random() * others.length)];
    }
  }
  const pick =
    EXPLORE_UNKNOWN_FAIR_BATTLE_MUSIC_PATHS[
      Math.floor(Math.random() * EXPLORE_UNKNOWN_FAIR_BATTLE_MUSIC_PATHS.length)
    ];
  if (!pick) {
    throw new Error('Explore Unknown fair battle music: track path selection failed');
  }
  return pick;
}

// Faction to music track(s); value may be a path string or a frozen array of variant paths.
const factionMusicMap = {
  'Asrah': '/music/asrah_1.mp3',
  'Remnants of Humanity': '/music/remnants.mp3',
  'Exolot Dominion': '/music/exolot_1.mp3',
  'League of Shadows': '/music/shadow_league.mp3',
  'Ra\'s Children': RA_CHILDREN_BATTLE_MUSIC_PATHS
};

function getFactionTrackPaths(faction) {
  const entry = factionMusicMap[faction];
  if (!entry) return null;
  return Array.isArray(entry) ? entry : [entry];
}

function pickFactionTrack(faction, previousTrack = null) {
  const paths = getFactionTrackPaths(faction);
  if (!paths || paths.length === 0) return null;
  if (paths.length === 1) return paths[0];
  if (previousTrack) {
    const others = paths.filter((path) => path !== previousTrack);
    if (others.length > 0) {
      return others[Math.floor(Math.random() * others.length)];
    }
  }
  return paths[Math.floor(Math.random() * paths.length)];
}

function shouldRotateTrackOnEnd(faction) {
  if (!faction) return false;
  if (combinedFactions[faction]) return true;
  const paths = getFactionTrackPaths(faction);
  return paths != null && paths.length > 1;
}

/**
 * Determine faction from strategy orders
 * @param {Object} strategy - Strategy object with orders array
 * @returns {string|null} - Faction name or null
 */
function determineFactionFromStrategy(strategy) {
  if (!strategy || !strategy.orders || !Array.isArray(strategy.orders)) {
    return null;
  }

  // Count factions from orders
  const factionCounts = {};
  strategy.orders.forEach((order) => {
    const faction = normalizeFactionName(order.faction_name || order.faction);
    if (faction && faction !== 'The Sentients') {
      factionCounts[faction] = (factionCounts[faction] || 0) + 1;
    }
  });

  const factions = Object.keys(factionCounts);
  if (factions.length === 0) return null;

  // Check if it matches a combined faction
  for (const [combinedName, componentFactions] of Object.entries(combinedFactions)) {
    const hasAllComponents = componentFactions.every(faction => factions.includes(faction));
    const onlyHasComponents = factions.every(faction => 
      componentFactions.includes(faction) || faction === combinedName
    );
    
    if (hasAllComponents && onlyHasComponents) {
      return combinedName;
    }
  }

  // If not a combined faction, return the most common faction
  const sortedFactions = factions.sort((a, b) => factionCounts[b] - factionCounts[a]);
  return sortedFactions[0];
}

/**
 * Get music track for a faction (handles combined factions with random selection)
 * @param {string} faction - Faction name
 * @param {string|null} previousTrack - Previous track path (to avoid selecting the same track)
 * @returns {string} - Music track path
 */
function getTrackForFaction(faction, previousTrack = null) {
  const canonicalFaction = normalizeFactionName(faction);
  if (!canonicalFaction) return null;

  // Check if it's a combined faction
  if (combinedFactions[canonicalFaction]) {
    const componentFactions = combinedFactions[canonicalFaction];
    
    // If we have a previous track, try to select a different one
    if (previousTrack && componentFactions.length > 1) {
      // Find which component faction corresponds to the previous track
      const previousComponent = Object.entries(factionMusicMap).find(([, trackPathOrPaths]) => {
        const paths = Array.isArray(trackPathOrPaths) ? trackPathOrPaths : [trackPathOrPaths];
        return paths.includes(previousTrack);
      })?.[0];
      
      // If we found the previous component, filter it out and select from remaining
      if (previousComponent && componentFactions.includes(previousComponent)) {
        const otherComponents = componentFactions.filter(f => f !== previousComponent);
        if (otherComponents.length > 0) {
          // Select from the other components (ensures different track)
          const randomIndex = Math.floor(Math.random() * otherComponents.length);
          const selectedComponent = otherComponents[randomIndex];
          return pickFactionTrack(selectedComponent, previousTrack);
        }
      }
    }
    
    // No previous track or couldn't determine it - randomly select any component
    const randomIndex = Math.floor(Math.random() * componentFactions.length);
    const selectedComponent = componentFactions[randomIndex];
    return pickFactionTrack(selectedComponent, previousTrack);
  }

  // Direct faction mapping
  return pickFactionTrack(canonicalFaction, previousTrack);
}

let hasValidatedFactionMusicCoverage = false;

function validateFactionMusicCoverage() {
  if (hasValidatedFactionMusicCoverage) return;
  hasValidatedFactionMusicCoverage = true;

  const factionsWithoutMusic = KNOWN_FACTIONS
    .filter((faction) => faction !== 'The Sentients')
    .filter((faction) => !getTrackForFaction(faction));

  if (factionsWithoutMusic.length > 0) {
    console.error(
      `[Music] Missing track mapping for factions: ${factionsWithoutMusic.join(', ')}`
    );
  }
}

/**
 * Custom hook for battle music based on opponent faction, tutorial mission, or Explore Unknown boss mission.
 * @param {Object} strategyDetails - Strategy object with orders
 * @param {number} fadeDuration - Fade in/out duration in milliseconds (default: 2000)
 * @param {{ missionId?: string, exploreUnknownMissionIndex?: number }} options
 *   - `missionId`: if it matches one of the 4 tutorial missions, play rock_2.mp3.
 *   - `exploreUnknownMissionIndex`: global Explore Unknown mission index. If it is a boss mission
 *     (per `EXPLORE_UNKNOWN_PORTAL_LAYOUT.bossMissionIndices`), play `/music/explore_boss.mp3`
 *     instead of the faction track. If it is on the fairgrounds slice (`shadowverse_fair.jpg` missions),
 *     play `/music/fair1.mp3` or `/music/fair2.mp3` at random (stable for that mission index).
 */
export function useBattleMusic(strategyDetails, fadeDuration = 2000, options = {}) {
  useEffect(() => {
    validateFactionMusicCoverage();
  }, []);

  const exploreUnknownMissionIndex =
    options.exploreUnknownMissionIndex != null
      ? Number(options.exploreUnknownMissionIndex)
      : null;
  const isTutorialMission = options.missionId && TUTORIAL_MISSION_IDS.includes(options.missionId);
  const isExploreBossMission = isExploreUnknownBossMissionIndex(exploreUnknownMissionIndex);
  const isExploreKendrexColonyMission = isExploreUnknownKendrexColonyMissionIndex(
    exploreUnknownMissionIndex
  );
  const isExploreFairMission =
    !isExploreBossMission &&
    !isExploreKendrexColonyMission &&
    isExploreUnknownFairSliceMissionIndex(exploreUnknownMissionIndex);
  const exploreUnknownFairBattleMusicRef = useRef({ missionIndex: null, trackPath: null });
  const audioRef = useRef(null);
  const fadeIntervalRef = useRef(null);
  const userInteractionHandlerRef = useRef(null);
  const isPlayingRef = useRef(false);
  const healthCheckIntervalRef = useRef(null);
  const retryCountRef = useRef(0);
  const lastRetryTimeRef = useRef(0);
  const currentFactionRef = useRef(null);
  const currentTrackRef = useRef(null);
  const [currentTrack, setCurrentTrack] = useState(null);
  const { musicEnabled, musicVolume, masterVolume } = useMusicContext();
  const effectiveMusicVolume = Math.min(1, musicVolume * masterVolume);
  // Main battle-music effect intentionally omits volume from its dependency array (avoid restarting
  // playback). Fade-in / track-switch logic must still read the latest user volume — otherwise a stale
  // closure makes each new track ramp to the old target (often full volume).
  const effectiveMusicVolumeRef = useRef(effectiveMusicVolume);
  effectiveMusicVolumeRef.current = effectiveMusicVolume;
  const resolvedFaction = strategyDetails ? determineFactionFromStrategy(strategyDetails) : null;
  const shouldHaveMusicContext =
    isTutorialMission || isExploreBossMission || isExploreFairMission || !!resolvedFaction;

  useEffect(() => {
    if (!shouldHaveMusicContext || !musicEnabled || isExploreKendrexColonyMission) {
      // If music is disabled, stop any playing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.volume = 0;
      }
      return;
    }

    // Track resolution priority:
    //   1) Explore Unknown boss mission → dedicated boss track (`explore_boss.mp3`).
    //   2) Explore Unknown fairgrounds slice → `fair1.mp3` or `fair2.mp3` (random per mission index).
    //   3) Tutorial missions 1–4 → `rock_2.mp3`.
    //   4) Faction-based selection (kept stable across UI transitions like mulligan end).
    let trackPath;
    if (isExploreBossMission) {
      trackPath = EXPLORE_BOSS_MUSIC_TRACK;
    } else if (isExploreFairMission) {
      const mi = exploreUnknownMissionIndex;
      const cache = exploreUnknownFairBattleMusicRef.current;
      if (cache.missionIndex === mi && cache.trackPath) {
        trackPath = cache.trackPath;
      } else {
        const pick = pickExploreUnknownFairBattleTrackPath(cache.trackPath);
        trackPath = pick;
        exploreUnknownFairBattleMusicRef.current = { missionIndex: mi, trackPath: pick };
      }
    } else if (isTutorialMission) {
      trackPath = TUTORIAL_MUSIC_TRACK;
    } else {
      const faction = resolvedFaction;
      if (!faction) {
        trackPath = null;
      } else {
        const sameFactionAsCurrent = currentFactionRef.current === faction;
        if (sameFactionAsCurrent && currentTrackRef.current) {
          trackPath = currentTrackRef.current;
        } else {
          trackPath = getTrackForFaction(faction);
        }
      }
    }

    if (!trackPath) return;

    incrementTrackRetain(trackPath);
    cancelScheduledTrackStop(trackPath);

    if (isExploreBossMission || isTutorialMission || isExploreFairMission) {
      // Boss / fairgrounds / tutorial tracks are not bound to a faction; clear the cached faction so
      // returning to a faction mission later forces a fresh selection rather than reusing the boss
      // track.
      currentFactionRef.current = null;
    } else {
      currentFactionRef.current = resolvedFaction;
    }

    currentTrackRef.current = trackPath;
    setCurrentTrack(trackPath);

    // Get cached audio element
    const audio = getCachedAudio(trackPath);
    
    // Register this audio element for tracking
    registerAudioElement(audio);
    
    audio.volume = 0;
    // Tutorial / boss / fairgrounds: loop one track; otherwise handle track end for combined factions.
    audio.loop = isTutorialMission || isExploreBossMission || isExploreFairMission;

    audioRef.current = audio;
    let fadeIntervalId = null;
    let releaseLock = null;

    // Fade out function that returns a promise
    const fadeOutAudio = (audioToFade, fadeDurationMs) => {
      return new Promise((resolve) => {
        if (!audioToFade || audioToFade.paused || audioToFade.volume === 0) {
          // Already stopped or at 0 volume, resolve immediately
          resolve();
          return;
        }

        const steps = 50;
        const currentVolume = audioToFade.volume;
        const volumeStep = currentVolume / steps;
        const stepDuration = fadeDurationMs / steps;
        let currentStep = 0;

        const fadeOutInterval = setInterval(() => {
          currentStep++;
          const newVolume = Math.max(currentVolume - (volumeStep * currentStep), 0);
          audioToFade.volume = newVolume;

          if (currentStep >= steps || newVolume <= 0) {
            clearInterval(fadeOutInterval);
            audioToFade.pause();
            audioToFade.currentTime = 0;
            audioToFade.volume = 0;
            resolve();
          }
        }, stepDuration);
      });
    };

    // Start the new track after waiting for any ongoing fade-out
    const startNewTrack = async () => {
      // CRITICAL: Acquire lock to prevent concurrent track starts
      releaseLock = await acquireTrackStartLock();
      
      try {
        // Double-check: Stop ALL audio as a safety measure
        stopAllAudio();
        
        // Wait a tiny bit to ensure all stops are processed
        await new Promise(resolve => setTimeout(resolve, 10));

        // If there's a currently playing audio that's different from the new one, fade it out
        const currentAudio = getCurrentPlayingAudio();
        if (currentAudio && currentAudio !== audio && !currentAudio.paused) {
          const fadeOutPromise = fadeOutAudio(currentAudio, fadeDuration);
          setCurrentFadeOutPromise(fadeOutPromise);
          await fadeOutPromise;
        }

        // Now set the new audio as current
        setCurrentPlayingAudio(audio);
      } catch (error) {
        // Release lock on error
        if (releaseLock) {
          releaseLock();
          releaseLock = null;
        }
        throw error;
      }
    };

    // Fade in
    const fadeIn = () => {
      if (isPlayingRef.current) return;

      const targetVolume = effectiveMusicVolumeRef.current;
      const steps = 50;
      const volumeStep = targetVolume / steps;
      const stepDuration = fadeDuration / steps;
      let currentStep = 0;

      fadeIntervalId = setInterval(() => {
        currentStep++;
        const newVolume = Math.min(volumeStep * currentStep, targetVolume);
        audio.volume = newVolume;

        if (currentStep >= steps) {
          clearInterval(fadeIntervalId);
          fadeIntervalId = null;
          audio.volume = targetVolume;
        }
      }, stepDuration);

      fadeIntervalRef.current = fadeIntervalId;
      isPlayingRef.current = true;
    };

    // Handle track end - combined factions and multi-track factions pick a new variant
    const handleTrackEnd = async () => {
      const faction = currentFactionRef.current;
      if (!faction || !shouldRotateTrackOnEnd(faction)) {
        // Not a combined faction, just loop
        try {
          audio.currentTime = 0;
          // MOBILE FIX: Ensure audio is ready before playing
          if (audio.paused) {
            await audio.play();
          } else {
            // If already playing, pause first then restart
            audio.pause();
            audio.currentTime = 0;
            await audio.play();
          }
        } catch (error) {
          // MOBILE FIX: Handle autoplay restrictions on mobile
          if (error.name === 'NotAllowedError' || error.name === 'NotSupportedError') {
            console.log('Battle music: Autoplay blocked, waiting for user interaction...');
            // Set up user interaction handler to restart
            if (!userInteractionHandlerRef.current) {
              const handleUserInteraction = async () => {
                try {
                  audio.currentTime = 0;
                  await audio.play();
                } catch (err) {
                  console.error('Error restarting battle music after user interaction:', err);
                  scheduleRetry();
                }
              };
              userInteractionHandlerRef.current = handleUserInteraction;
              document.addEventListener('click', handleUserInteraction, { once: true });
              document.addEventListener('touchstart', handleUserInteraction, { once: true });
              document.addEventListener('keydown', handleUserInteraction, { once: true });
            }
          } else {
            console.error('Error restarting battle music:', error);
            scheduleRetry();
          }
        }
        return;
      }

      // Combined faction - select new random track (ensures different track if possible)
      const newTrackPath = getTrackForFaction(faction, currentTrackRef.current);
      
      if (newTrackPath) {
        // If it's the same track (shouldn't happen with the fix, but handle it anyway)
        if (newTrackPath === currentTrackRef.current) {
          // This should be rare now, but if it happens, just restart
          try {
            audio.currentTime = 0;
            if (audio.paused) {
              await audio.play();
            } else {
              audio.pause();
              audio.currentTime = 0;
              await audio.play();
            }
          } catch (error) {
            console.error('Error restarting same track:', error);
            scheduleRetry();
          }
          return;
        }

        // Different track - fade out current, then fade in new
        const fadeOut = () => {
          const steps = 50;
          const currentVolume = audio.volume;
          const volumeStep = currentVolume / steps;
          const stepDuration = fadeDuration / steps;
          let currentStep = 0;

          const fadeOutInterval = setInterval(() => {
            currentStep++;
            const newVolume = Math.max(currentVolume - (volumeStep * currentStep), 0);
            audio.volume = newVolume;

            if (currentStep >= steps || newVolume <= 0) {
              clearInterval(fadeOutInterval);
              audio.pause();
              audio.removeEventListener('ended', handleTrackEnd);
              
              // Switch to new track
              const newAudio = getCachedAudio(newTrackPath);
              
              // Register new audio element
              registerAudioElement(newAudio);
              
              // CRITICAL: Stop all audio before switching
              stopAllAudio();
              
              newAudio.volume = 0;
              newAudio.loop = false;
              newAudio.currentTime = 0;
              
              // Update refs and global state
              audioRef.current = newAudio;
              currentTrackRef.current = newTrackPath;
              setCurrentTrack(newTrackPath);
              setCurrentPlayingAudio(newAudio);
              
              // Start new track with fade in
              const startNewTrackInternal = async () => {
                try {
                  // Ensure audio is stopped before playing
                  if (!newAudio.paused) {
                    newAudio.pause();
                  }
                  
                  await newAudio.play();
                  
                  // Reset retry count on successful track switch
                  retryCountRef.current = 0;
                  lastRetryTimeRef.current = 0;
                  
                  // Set up new track end handler
                  newAudio.addEventListener('ended', handleTrackEnd);
                  
                  // Fade in (use ref so volume matches settings even long after the effect was created)
                  const targetVolume = effectiveMusicVolumeRef.current;
                  const steps = 50;
                  const volumeStep = targetVolume / steps;
                  const stepDuration = fadeDuration / steps;
                  let currentStep = 0;

                  const fadeInInterval = setInterval(() => {
                    currentStep++;
                    const newVolume = Math.min(volumeStep * currentStep, targetVolume);
                    newAudio.volume = newVolume;

                    if (currentStep >= steps) {
                      clearInterval(fadeInInterval);
                      newAudio.volume = targetVolume;
                    }
                  }, stepDuration);
                } catch (error) {
                  // Handle autoplay restriction
                  if (error.name === 'NotAllowedError' || error.name === 'NotSupportedError') {
                    const handleUserInteraction = async () => {
                      try {
                        await startNewTrackInternal();
                      } catch (err) {
                        console.error('Error playing new track after user interaction:', err);
                        scheduleRetry();
                      }
                    };
                    userInteractionHandlerRef.current = handleUserInteraction;
                    document.addEventListener('click', handleUserInteraction, { once: true });
                    document.addEventListener('touchstart', handleUserInteraction, { once: true });
                    document.addEventListener('keydown', handleUserInteraction, { once: true });
                  } else {
                    console.error('Error starting new track:', error);
                    scheduleRetry();
                  }
                }
              };
              
              startNewTrackInternal();
            }
          }, stepDuration);
        };

        fadeOut();
      } else {
        // No new track, just restart current
        try {
          audio.currentTime = 0;
          if (audio.paused) {
            await audio.play();
          } else {
            audio.pause();
            audio.currentTime = 0;
            await audio.play();
          }
        } catch (error) {
          console.error('Error restarting track:', error);
          scheduleRetry();
        }
      }
    };

    // Start playing with retry logic
    const startPlaying = async (isRetry = false) => {
      if (!isRetry && isPlayingRef.current && !audio.paused && getCurrentPlayingAudio() === audio) {
        return;
      }

      try {
        // Wait for fade-out and start new track (acquires lock)
        await startNewTrack();

        // CRITICAL: Verify no other audio is playing before starting
        const currentAudio = getCurrentPlayingAudio();
        if (currentAudio && currentAudio !== audio && !currentAudio.paused) {
          // Emergency stop if something else is playing
          stopAllAudio();
          await new Promise(resolve => setTimeout(resolve, 50));
        }

        // Ensure audio is stopped before playing
        if (!audio.paused) {
          audio.pause();
        }
        audio.volume = 0;

        await audio.play();
        fadeIn();
        
        // Reset retry count on success
        retryCountRef.current = 0;
        lastRetryTimeRef.current = 0;
        
        // Release lock after successfully starting
        if (releaseLock) {
          releaseLock();
          releaseLock = null;
        }
        
        // Remove user interaction listener if it was set up
        if (userInteractionHandlerRef.current) {
          document.removeEventListener('click', userInteractionHandlerRef.current);
          document.removeEventListener('touchstart', userInteractionHandlerRef.current);
          document.removeEventListener('keydown', userInteractionHandlerRef.current);
          userInteractionHandlerRef.current = null;
        }

        // Listen for track end
        audio.addEventListener('ended', handleTrackEnd);
      } catch (error) {
        if (error.name === 'NotAllowedError' || error.name === 'NotSupportedError') {
          if (!userInteractionHandlerRef.current) {
            const handleUserInteraction = async () => {
              try {
                await startPlaying();
                // Retry count is reset in startPlaying on success
              } catch (err) {
                console.error('Error playing battle music after user interaction:', err);
                scheduleRetry();
              }
            };
            
            userInteractionHandlerRef.current = handleUserInteraction;
            document.addEventListener('click', handleUserInteraction, { once: true });
            document.addEventListener('touchstart', handleUserInteraction, { once: true });
            document.addEventListener('keydown', handleUserInteraction, { once: true });
          }
        } else {
          console.error('Error playing battle music:', error);
          scheduleRetry();
        }
      }
    };

    // Retry logic with exponential backoff
    const scheduleRetry = () => {
      const now = Date.now();
      const timeSinceLastRetry = now - lastRetryTimeRef.current;
      
      // Exponential backoff: 1s, 2s, 4s, 8s, max 10s
      const maxRetries = 5;
      if (retryCountRef.current >= maxRetries) {
        console.warn('Battle music retry limit reached. Giving up.');
        return;
      }
      
      const backoffDelay = Math.min(1000 * Math.pow(2, retryCountRef.current), 10000);
      
      // Don't retry too frequently
      if (timeSinceLastRetry < backoffDelay) {
        return;
      }
      
      retryCountRef.current++;
      lastRetryTimeRef.current = now;
      
      console.log(`Retrying battle music playback (attempt ${retryCountRef.current}/${maxRetries})...`);
      
      setTimeout(() => {
        // Retry whenever music should be active and this audio is not actually playing.
        // Important: playback can fail after this audio becomes "current", so checking only
        // current-audio identity can incorrectly suppress retries.
        const isActuallyPlaying = audio && !audio.paused && audio.volume > 0;
        if (musicEnabled && shouldHaveMusicContext && !isActuallyPlaying) {
          startPlaying(true).catch(err => {
            console.error('Battle music retry failed:', err);
            if (retryCountRef.current < maxRetries) {
              scheduleRetry();
            }
          });
        }
      }, backoffDelay);
    };

    // Health check: verify music is actually playing when it should be
    // Also checks for track end (mobile devices may not fire 'ended' event reliably)
    const startHealthCheck = () => {
      // Clear any existing health check
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current);
      }
      
      healthCheckIntervalRef.current = setInterval(() => {
        // Only check if music is enabled and we have strategy details
        if (!musicEnabled || !shouldHaveMusicContext) {
          return;
        }
        
        // Check if audio should be playing but isn't
        const shouldBePlaying = getCurrentPlayingAudio() === audio;
        const isActuallyPlaying = audio && !audio.paused && audio.volume > 0;
        
        // MOBILE FIX: Check if track has ended (ended event might not fire on mobile)
        const hasEnded = audio && audio.duration > 0 && 
                        (audio.currentTime >= audio.duration - 0.1 || 
                         (audio.ended && !audio.paused));
        
        if (hasEnded && shouldBePlaying) {
          console.log('Battle music health check: Track ended detected. Restarting...');
          // Track ended - trigger handleTrackEnd
          handleTrackEnd();
          return;
        }
        
        if (shouldBePlaying && !isActuallyPlaying && !hasEnded) {
          console.warn('Battle music health check: Music should be playing but isn\'t. Attempting recovery...');
          
          // Reset retry count for health check recovery
          retryCountRef.current = 0;
          lastRetryTimeRef.current = 0;
          
          // Try to recover immediately, then let backoff retries continue if needed.
          startPlaying(true).catch(err => {
            console.error('Battle music health check recovery failed:', err);
            scheduleRetry();
          });
        }
      }, 1000); // Check every 1 second (more frequent for better mobile support)
    };

    startPlaying();
    
    // Start health check
    startHealthCheck();

    // Additional recovery hook for tab/app focus changes.
    // Some environments pause or block audio; restart when focus returns.
    const handleWindowFocusRecovery = () => {
      if (!musicEnabled || !shouldHaveMusicContext) return;
      const isActuallyPlaying = audio && !audio.paused && audio.volume > 0;
      if (!isActuallyPlaying) {
        retryCountRef.current = 0;
        lastRetryTimeRef.current = 0;
        startPlaying(true).catch(err => {
          console.error('Battle music focus recovery failed:', err);
          scheduleRetry();
        });
      }
    };
    window.addEventListener('focus', handleWindowFocusRecovery);
    document.addEventListener('visibilitychange', handleWindowFocusRecovery);

    const retainedTrackPath = trackPath;

    // Cleanup
    return () => {
      decrementTrackRetain(retainedTrackPath);

      window.removeEventListener('focus', handleWindowFocusRecovery);
      document.removeEventListener('visibilitychange', handleWindowFocusRecovery);

      // Clear health check
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current);
        healthCheckIntervalRef.current = null;
      }
      if (userInteractionHandlerRef.current) {
        document.removeEventListener('click', userInteractionHandlerRef.current);
        document.removeEventListener('touchstart', userInteractionHandlerRef.current);
        document.removeEventListener('keydown', userInteractionHandlerRef.current);
        userInteractionHandlerRef.current = null;
      }

      if (fadeIntervalRef.current) {
        clearInterval(fadeIntervalRef.current);
        fadeIntervalRef.current = null;
      }
      if (fadeIntervalId) {
        clearInterval(fadeIntervalId);
        fadeIntervalId = null;
      }

      if (audio) {
        audio.removeEventListener('ended', handleTrackEnd);
      }

      isPlayingRef.current = false;
      retryCountRef.current = 0;
      lastRetryTimeRef.current = 0;
      
      // Release lock if held
      if (releaseLock) {
        releaseLock();
        releaseLock = null;
      }
      
      // Unregister audio element
      if (audio) {
        unregisterAudioElement(audio);
      }

      const runStopCleanup = () => {
        if (audio && getCurrentPlayingAudio() === audio) {
          const fadeOutPromise = fadeOutAudio(audio, fadeDuration);
          setCurrentFadeOutPromise(fadeOutPromise);
          fadeOutPromise
            .then(() => {
              if (getCurrentPlayingAudio() === audio) {
                setCurrentPlayingAudio(null);
              }
            })
            .catch(() => {
              if (getCurrentPlayingAudio() === audio) {
                setCurrentPlayingAudio(null);
              }
            });
        } else if (audio && !audio.paused) {
          try {
            audio.pause();
            audio.currentTime = 0;
            audio.volume = 0;
          } catch (e) {
            console.warn('Error stopping untracked battle audio:', e);
          }
        }
      };

      scheduleTrackStopIfUnretained(retainedTrackPath, runStopCleanup);
    };
  }, [
    resolvedFaction,
    fadeDuration,
    musicEnabled,
    isTutorialMission,
    isExploreBossMission,
    isExploreKendrexColonyMission,
    isExploreFairMission,
    shouldHaveMusicContext,
    // Fairgrounds: consecutive missions are all `isExploreFairMission === true`; index must retrigger the effect.
    exploreUnknownMissionIndex,
  ]);

  // Update volume when music or master volume changes (for already playing audio)
  useEffect(() => {
    if (!audioRef.current || !musicEnabled) return;
    
    const audio = audioRef.current;
    // Only update if this audio is currently playing
    if (getCurrentPlayingAudio() === audio && !audio.paused) {
      // Smoothly transition to new volume
      const currentVolume = audio.volume;
      const targetVolume = effectiveMusicVolume;
      const steps = 20;
      const volumeDiff = targetVolume - currentVolume;
      const volumeStep = volumeDiff / steps;
      let currentStep = 0;
      
      const volumeInterval = setInterval(() => {
        currentStep++;
        const newVolume = currentVolume + (volumeStep * currentStep);
        audio.volume = Math.max(0, Math.min(1, newVolume));
        
        if (currentStep >= steps) {
          clearInterval(volumeInterval);
          audio.volume = targetVolume;
        }
      }, 20);
      
      return () => clearInterval(volumeInterval);
    } else if (getCurrentPlayingAudio() === audio) {
      // If it's the current audio but paused, just set the volume directly
      audio.volume = effectiveMusicVolume;
    }
  }, [musicVolume, masterVolume, musicEnabled, effectiveMusicVolume]);

  return { audioRef, currentTrack };
}

